/**
 * LocalStorage helpers for prayer location favorites & history.
 * Stored as `{variant, slug, kecSlug?}` JSON.
 */

const FAV_KEY = "prayer:favs";
const HIST_KEY = "prayer:history";

export interface PrayerLink {
  variant: "sholat" | "imsak" | "buka-puasa";
  slug: string;
  kecSlug?: string;
  name: string;
  href: string;
}

function read(key: string): PrayerLink[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(window.localStorage.getItem(key) ?? "[]") as PrayerLink[];
  } catch {
    return [];
  }
}
function write(key: string, list: PrayerLink[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(list.slice(0, 30)));
  window.dispatchEvent(new Event("prayer:store"));
}

function eq(a: PrayerLink, b: PrayerLink) {
  return a.variant === b.variant && a.slug === b.slug && (a.kecSlug ?? "") === (b.kecSlug ?? "");
}

export function getFavorites(): PrayerLink[] { return read(FAV_KEY); }
export function isFavorite(item: PrayerLink): boolean { return getFavorites().some((x) => eq(x, item)); }
export function toggleFavorite(item: PrayerLink): boolean {
  const list = getFavorites();
  const idx = list.findIndex((x) => eq(x, item));
  if (idx >= 0) { list.splice(idx, 1); write(FAV_KEY, list); return false; }
  list.unshift(item); write(FAV_KEY, list); return true;
}
export function getHistory(): PrayerLink[] { return read(HIST_KEY); }
export function pushHistory(item: PrayerLink) {
  const list = getHistory().filter((x) => !eq(x, item));
  list.unshift(item); write(HIST_KEY, list.slice(0, 10));
}
export function clearHistory() { write(HIST_KEY, []); }

export function removeHistory(item: PrayerLink) {
  const list = getHistory().filter((x) => !eq(x, item));
  write(HIST_KEY, list);
}

const DEFAULT_KEY = "prayer:default";
export function setDefaultLocation(item: PrayerLink) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(DEFAULT_KEY, JSON.stringify(item));
  window.dispatchEvent(new Event("prayer:store"));
}
export function getDefaultLocation(variant: "sholat" | "imsak" | "buka-puasa"): PrayerLink | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DEFAULT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PrayerLink;
    return parsed.variant === variant ? parsed : null;
  } catch { return null; }
}
export function clearDefaultLocation() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(DEFAULT_KEY);
  window.dispatchEvent(new Event("prayer:store"));
}
